import type AsyncStatus from 'mini-common-schema/lib/schema/AsyncStatus';
import React, { useEffect, useRef, useState } from 'react';
import { type CalendarRoutes, type MailRoutes, type PeopleRoutes } from 'mini-routes';
import { matchPath, useLocation, useNavigate } from 'mini-react-router-dom';
import { type Module } from 'mini-common-schema';
import { useMiniNavigation } from 'mini-navigation/lib/MiniNavigationContext';
import type { Location } from 'mini-react-router-dom';

const moduleHomePageRoutesMap = {
    [0]: ['/', '/inbox'],
    [1]: ['/', '/view/:view'],
    [2]: ['/', '/:folderId'],
    [3]: ['/'],
};

export interface RouteSanitizerProps {
    module: Module;
}

/**
 * Handles insertion of module homepage route in the history stack when a non-homepage route is opened directly.
 */
export const RouteSanitizer: React.FC<RouteSanitizerProps> = props => {
    const { module, children } = props;
    const homePageRoutes = moduleHomePageRoutesMap[module];
    const [fallbackHandleStatus, setFallbackHandleStatus] = useState<AsyncStatus>(0);
    const { currentEntry } = useMiniNavigation();
    const location = useLocation();
    const navigate = useNavigate();
    const originalLocation = useRef<Location>(location);

    useEffect(() => {
        const { index } = currentEntry;
        const { pathname } = location;

        if (fallbackHandleStatus === 0) {
            setFallbackHandleStatus(7);
            const isHomePageRoute = homePageRoutes.some(
                route => !!matchPath({ path: route, end: true }, pathname)
            );
            // If not homepage route, we need to replace current route with homepage route. Otherwise nothing.
            if (index === 0 && !isHomePageRoute) {
                navigate(homePageRoutes[0], { replace: true });
            } else {
                setFallbackHandleStatus(8);
            }
        } else if (fallbackHandleStatus === 7) {
            const isHomePageRoute = !!matchPath({ path: homePageRoutes[0], end: true }, pathname);
            // Once we land on the replaced homepage route, push the route we are supposed to go in the first place
            if (index === 0 && isHomePageRoute) {
                navigate(originalLocation.current);
            }
            setFallbackHandleStatus(8);
        }
    }, [fallbackHandleStatus, currentEntry, navigate, location, homePageRoutes]);

    return fallbackHandleStatus === 8 ? <>{children}</> : null;
};
